import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Space, Typography, Divider, Spin, Popconfirm, message as antMessage } from 'antd';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useUser } from '../../contexts/UserContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { UIRole, UI_ROLE_OPTIONS } from '../../types/uiRoles';
import { ModelType, MODEL_TYPE_OPTIONS, ModelSelection } from '../../types/models';
import { ThreadModelService, BusinessModel } from '../../services/api/threadModelService';
import { PrimeArtifact } from '../../types/primeModel';
import { ArtifactTree } from '../../types/artifacts';

const { Text } = Typography;
const { Option } = Select;

interface NewThreadDialogProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (threadData: { name: string; model: ModelSelection; uiRole: string; description?: string }) => void;
  onDelete?: (threadId: string) => Promise<void>;
  initialData?: {
    id?: string;
    name?: string;
    model?: ModelSelection;
    uiRole?: string;
    description?: string;
    messageCount?: number;
  };
}

export const NewThreadDialog: React.FC<NewThreadDialogProps> = ({
  visible,
  onCancel,
  onOk,
  onDelete,
  initialData,
}) => {
  const { uiRole: currentUIRole, environment: currentEnvironment } = useModelSelector();
  const { selectedCompany, availableCompanies } = useUser();
  const { model: chatUIModel, uiRole: chatUIRole } = useChatUI();
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    modelType: (initialData?.model?.type?.toLowerCase() as ModelType) || (chatUIModel?.type || ModelType.COMPANY),
    modelArtifactId: initialData?.model?.artifactId || (chatUIModel?.type === ModelType.PRIME ? chatUIModel.artifactId : ''),
    modelArtifactTreeId: initialData?.model?.artifactTreeId || (chatUIModel?.type === ModelType.COMPANY ? chatUIModel.artifactTreeId : ''),
    modelBusinessModelId: initialData?.model?.businessModelId || '',
    uiRole: initialData?.uiRole || chatUIRole || currentUIRole,
    description: initialData?.description || '',
  });

  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [companyTrees, setCompanyTrees] = useState<ArtifactTree[]>([]);
  const [businessModels, setBusinessModels] = useState<BusinessModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [formMounted, setFormMounted] = useState(false);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      const newFormData = {
        name: initialData.name || '',
        modelType: (initialData.model?.type?.toLowerCase() as ModelType) || (chatUIModel?.type || ModelType.COMPANY),
        modelArtifactId: initialData.model?.artifactId || (chatUIModel?.type === ModelType.PRIME ? chatUIModel.artifactId : ''),
        modelArtifactTreeId: initialData.model?.artifactTreeId || (chatUIModel?.type === ModelType.COMPANY ? chatUIModel.artifactTreeId : ''),
        modelBusinessModelId: initialData.model?.businessModelId || '',
        uiRole: initialData.uiRole || chatUIRole || currentUIRole,
        description: initialData.description || '',
      };
      setFormData(newFormData);
      form.setFieldsValue(newFormData);
    }
  }, [initialData, form, currentUIRole]);

  // Mark form as mounted when dialog becomes visible
  useEffect(() => {
    if (visible) {
      // Use setTimeout to ensure Form component is fully mounted
      const timer = setTimeout(() => {
        setFormMounted(true);
      }, 0);
      return () => {
        clearTimeout(timer);
        setFormMounted(false);
      };
    } else {
      setFormMounted(false);
    }
  }, [visible]);

  // Load model options when dialog opens
  useEffect(() => {
    if (visible) {
      loadModelOptions();
    }
  }, [visible]);

  const loadModelOptions = async () => {
    setLoading(true);
    try {
      // Get all company IDs the user has access to
      const accessibleCompanyIds = availableCompanies.map(uc => uc.company.id);
      const { primeRoots, companyTrees: trees } = await ThreadModelService.getAllModelOptions(
        selectedCompany?.id,
        accessibleCompanyIds
      );
      setPrimeArtifacts(primeRoots);
      setCompanyTrees(trees);
    } catch (error) {
      console.error('Failed to load model options:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessModels = async (artifactTreeId: string) => {
    setLoading(true);
    try {
      // TODO: Create API endpoint to fetch business models for an artifact tree
      // For now, return empty array (will auto-select default)
      const models = await ThreadModelService.getBusinessModelsForTree(artifactTreeId);
      setBusinessModels(models);
      
      // Auto-select if only one business model
      if (models.length === 1) {
        setFormData(prev => ({ ...prev, modelBusinessModelId: models[0].id }));
        form.setFieldValue('modelBusinessModelId', models[0].id);
      }
    } catch (error) {
      console.error('Failed to load business models:', error);
      // If error or no models, clear selection
      setBusinessModels([]);
    } finally {
      setLoading(false);
    }
  };

  // Load business models when artifact tree is selected
  useEffect(() => {
    if (formData.modelType === ModelType.COMPANY && formData.modelArtifactTreeId) {
      loadBusinessModels(formData.modelArtifactTreeId);
    } else {
      setBusinessModels([]);
    }
  }, [formData.modelArtifactTreeId, formData.modelType]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const model: ModelSelection = {
        type: values.modelType,
        ...(values.modelType === ModelType.PRIME && { artifactId: values.modelArtifactId }),
        ...(values.modelType === ModelType.COMPANY && { 
          artifactTreeId: values.modelArtifactTreeId,
          ...(values.modelBusinessModelId && { businessModelId: values.modelBusinessModelId })
        }),
      };
      
      
      onOk({
        name: values.name,
        model,
        uiRole: values.uiRole,
        description: values.description,
      });
      
      // Only reset form if we're creating a new thread (not editing)
      if (!initialData) {
        form.resetFields();
        setFormData({
          name: '',
          modelType: ModelType.COMPANY,
          modelArtifactId: '',
          modelArtifactTreeId: '',
          modelBusinessModelId: '',
          uiRole: currentUIRole,
          description: '',
        });
      }
    });
  };

  const handleCancel = () => {
    // Only reset form if we're creating a new thread (not editing)
    if (!initialData) {
      form.resetFields();
      setFormData({
        name: '',
        modelType: ModelType.COMPANY,
        modelArtifactId: '',
        modelArtifactTreeId: '',
        modelBusinessModelId: '',
        uiRole: currentUIRole,
        description: '',
      });
    }
    onCancel();
  };

  const handleDelete = async () => {
    if (!initialData?.id || !onDelete) return;
    
    try {
      setLoading(true);
      await onDelete(initialData.id);
      antMessage.success('Thread and associated messages deleted successfully');
      onCancel();
    } catch (error) {
      antMessage.error('Failed to delete thread');
      console.error('Thread deletion error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyThreadId = () => {
    if (initialData?.id) {
      navigator.clipboard.writeText(initialData.id);
      antMessage.success('Thread ID copied to clipboard');
    }
  };

  const isEditing = !!initialData;

  return (
    <Modal
      title={isEditing ? "Edit Thread" : "Create New Thread"}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isEditing ? "Save Changes" : "Create Thread"}
      cancelText="Cancel"
      width={500}
      confirmLoading={loading}
      footer={[
        isEditing && onDelete && (
          <Popconfirm
            key="delete"
            title="Delete Thread"
            description="Are you sure you want to delete this thread? This will also delete all messages and commands associated with it."
            onConfirm={handleDelete}
            okText="Yes, Delete"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button 
              danger 
              icon={<DeleteOutlined />}
              loading={loading}
            >
              Delete Thread
            </Button>
          </Popconfirm>
        ),
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="ok"
          type="primary"
          onClick={handleOk}
          loading={loading}
        >
          {isEditing ? "Save Changes" : "Create Thread"}
        </Button>
      ]}
    >
      <Spin spinning={loading} tip="Loading model options...">
        {/* Thread Info Section - Only show when editing */}
        {isEditing && initialData?.id && (
          <div style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: '#f0f5ff',
            borderRadius: 6,
            border: '1px solid #adc6ff'
          }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ fontSize: 12, color: '#1890ff' }}>Thread ID:</Text>
                <Space>
                  <code style={{ 
                    fontSize: 11, 
                    backgroundColor: 'white', 
                    padding: '2px 6px', 
                    borderRadius: 4,
                    border: '1px solid #d9d9d9'
                  }}>
                    {initialData.id}
                  </code>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={handleCopyThreadId}
                    title="Copy Thread ID"
                  />
                </Space>
              </div>
              {initialData.messageCount !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 12, color: '#1890ff' }}>Messages:</Text>
                  <Text style={{ fontSize: 12 }}>{initialData.messageCount}</Text>
                </div>
              )}
              {initialData.model?.artifactTreeId && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 12, color: '#1890ff' }}>Artifact Tree:</Text>
                  <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>{initialData.model.artifactTreeId.substring(0, 8)}...</Text>
                </div>
              )}
              {initialData.model?.businessModelId && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 12, color: '#1890ff' }}>Business Model:</Text>
                  <Text style={{ fontSize: 11, fontFamily: 'monospace' }}>{initialData.model.businessModelId.substring(0, 8)}...</Text>
                </div>
              )}
            </Space>
          </div>
        )}
        
        <Form
          form={form}
          layout="vertical"
          initialValues={formData}
          onValuesChange={(_, values) => setFormData(values)}
          onFinish={() => {}} // Dummy handler to ensure form is considered "mounted"
        >
        <Form.Item
          name="name"
          label="Thread Name"
          rules={[{ required: true, message: 'Please enter a thread name' }]}
        >
          <Input placeholder="Enter thread name..." />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description (Optional)"
        >
          <Input.TextArea 
            placeholder="Enter thread description..." 
            rows={3}
          />
        </Form.Item>

        <Form.Item
          name="modelType"
          label="Model Type"
          rules={[{ required: true, message: 'Please select a model type' }]}
        >
          <Select placeholder="Select model type">
            {MODEL_TYPE_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {formData.modelType === ModelType.PRIME && (
          <Form.Item
            name="modelArtifactId"
            label="Prime Artifact"
            rules={[{ required: true, message: 'Please select a prime artifact' }]}
          >
            <Select 
              placeholder="Select prime artifact"
              loading={loading}
              disabled={loading}
            >
              {primeArtifacts.map(artifact => (
                <Option key={artifact.id} value={artifact.id}>
                  {artifact.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {formData.modelType === ModelType.COMPANY && (
          <Form.Item
            name="modelArtifactTreeId"
            label="Company Artifact Tree"
            rules={[{ required: true, message: 'Please select an artifact tree' }]}
          >
            <Select 
              placeholder="Select artifact tree"
              loading={loading}
              disabled={loading}
            >
              {companyTrees.map(tree => (
                <Option key={tree.id} value={tree.id}>
                  {tree.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {formData.modelType === ModelType.COMPANY && businessModels.length > 1 && (
          <Form.Item
            name="modelBusinessModelId"
            label="Business Model Scenario"
            rules={[{ required: false }]}
          >
            <Select 
              placeholder="Select business model (optional)"
              loading={loading}
              disabled={loading}
              allowClear
            >
              {businessModels.map(model => (
                <Option key={model.id} value={model.id}>
                  {model.name} {model.is_active && '(Active)'}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {formData.modelType === ModelType.COMPANY && businessModels.length === 1 && formData.modelBusinessModelId && (
          <Form.Item label="Business Model">
            <Input 
              value={businessModels[0]?.name || formData.modelBusinessModelId}
              disabled
              style={{ fontSize: 12, color: '#666' }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Only one business model in this artifact tree (auto-selected)
            </Text>
          </Form.Item>
        )}

        <Form.Item
          name="uiRole"
          label="UI Role"
          rules={[{ required: true, message: 'Please select a UI role' }]}
        >
          <Select placeholder="Select UI role">
            {UI_ROLE_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ 
          marginTop: 16, 
          padding: 12, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 6,
          border: '1px solid #d9d9d9'
        }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Current Environment:</strong> {currentEnvironment}
          </Text>
        </div>
        </Form>
      </Spin>
    </Modal>
  );
};
